import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { MemoryRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { parseStartParam, resolveLocale } from '@debtbot/shared';
import { isBotMoved, NetworkError } from '../api/errors';
import { ToastProvider } from '../components/Toast';
import { LocaleContext } from '../i18n';
import { AcceptInvitationScreen } from '../screens/AcceptInvitationScreen';
import { BotMovedScreen } from '../screens/BotMovedScreen';
import { FeedbackScreen } from '../screens/FeedbackScreen';
import { HomeScreen } from '../screens/HomeScreen';
import { PersonScreen } from '../screens/PersonScreen';
import { PickPersonScreen } from '../screens/PickPersonScreen';
import { SettingsScreen } from '../screens/SettingsScreen';
import { TransactionFormScreen } from '../screens/TransactionFormScreen';
import { TransactionScreen } from '../screens/TransactionScreen';
import type { TelegramEnv } from '../telegram/init';
import { BackButtonSync } from './BackButtonSync';
import { LocaleProvider } from './LocaleProvider';

/** onBotMoved — любой ответ 410 BOT_MOVED (Mini App открыт из прежнего бота) переключает всё приложение на экран переезда. */
function makeQueryClient(onBotMoved: () => void): QueryClient {
  const onError = (err: unknown) => {
    if (isBotMoved(err)) onBotMoved();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: (count, err) => err instanceof NetworkError && count < 1,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: 0 },
    },
  });
}

function Layout() {
  return (
    <>
      <BackButtonSync />
      <Outlet />
    </>
  );
}

/**
 * Deep link → стартовый стек маршрутов. Главный экран всегда внизу стека,
 * чтобы BackButton с целевого экрана вёл домой, а не закрывал приложение.
 */
function initialEntries(env: TelegramEnv): string[] {
  const p = parseStartParam(env.startParam);
  if (!p) return ['/'];
  switch (p.kind) {
    case 'invite':
      return ['/', `/invite/${p.token}`];
    case 'connection':
      return ['/', `/c/${p.id}`];
    case 'transaction':
      return ['/', `/tx/${p.id}`];
  }
}

export function App({ env }: { env: TelegramEnv }) {
  const [botMoved, setBotMoved] = useState(false);
  const [queryClient] = useState(() => makeQueryClient(() => setBotMoved(true)));
  if (botMoved) {
    // Без LocaleProvider: он ходит в /me, а /me и ответил 410. Язык — из initData.
    return (
      <LocaleContext.Provider value={resolveLocale(null, env.languageCode)}>
        <BotMovedScreen startParam={env.startParam} />
      </LocaleContext.Provider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider telegramLanguage={env.languageCode}>
        <ToastProvider>
          <MemoryRouter initialEntries={initialEntries(env)}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<HomeScreen />} />
                <Route path="/add" element={<PickPersonScreen />} />
                <Route path="/settings" element={<SettingsScreen />} />
                <Route path="/feedback" element={<FeedbackScreen />} />
                <Route path="/c/:id" element={<PersonScreen />} />
                <Route path="/c/:id/new" element={<TransactionFormScreen mode="new" />} />
                <Route path="/tx/:id" element={<TransactionScreen />} />
                <Route path="/tx/:id/edit" element={<TransactionFormScreen mode="edit" />} />
                <Route path="/invite/:token" element={<AcceptInvitationScreen />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </MemoryRouter>
        </ToastProvider>
      </LocaleProvider>
    </QueryClientProvider>
  );
}
